import type { KeyboardEvent, ReactNode } from "react";
import { cx } from "./cx";

export interface TableColumn<Row> {
  key: string;
  label: string;
  align?: "left" | "right" | "center";
  /** Render the cell in IBM Plex Mono. SKUs, quantities, prices. */
  mono?: boolean;
  width?: string | number;
  /** Cell content. Defaults to `row[key]`. */
  render?: (row: Row) => ReactNode;
}

export interface TableProps<Row> {
  columns: TableColumn<Row>[];
  rows: Row[];
  /** Row identity, and what `selectedRow` is matched against. */
  rowKey: (row: Row, index: number) => string | number;
  onRowClick?: (row: Row, index: number) => void;
  /** Key of the selected row: green fill and a green inset edge. */
  selectedRow?: string | number;
  className?: string;
}

const ALIGN = { left: "text-left", right: "text-right", center: "text-center" } as const;

/** Dense data table: 44px rows, uppercase Montserrat headers, mono numerics. */
export function Table<Row>({ columns, rows, rowKey, onRowClick, selectedRow, className }: TableProps<Row>) {
  const activate = (event: KeyboardEvent, row: Row, index: number) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onRowClick?.(row, index);
    }
  };

  return (
    // Scrolls sideways rather than clipping: on a narrow screen a hidden Qty column is worse
    // than a scroll. Phone screens should still prefer their own row layout (mobile kit PartRow).
    <div className={cx("overflow-x-auto rounded-md border border-border-subtle bg-surface-card shadow-xs", className)}>
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-surface-sunken">
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                style={{ width: column.width }}
                className={cx(
                  "border-b border-border-subtle px-3 py-2.5 type-eyebrow tracking-wider text-ink-500 uppercase",
                  ALIGN[column.align ?? "left"],
                )}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const key = rowKey(row, index);
            const selected = selectedRow !== undefined && key === selectedRow;
            return (
              <tr
                key={key}
                aria-selected={onRowClick ? selected : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                onClick={onRowClick ? () => onRowClick(row, index) : undefined}
                onKeyDown={onRowClick ? (event) => activate(event, row, index) : undefined}
                className={cx(
                  "[&>td]:border-b [&>td]:border-border-subtle last:[&>td]:border-b-0",
                  selected ? "bg-green-50" : "hover:bg-paper-1",
                  onRowClick && "cursor-pointer",
                )}
              >
                {columns.map((column, columnIndex) => (
                  <td
                    key={column.key}
                    className={cx(
                      "h-11 p-3 align-middle",
                      // A SKU or part number split across lines gets misread, so mono cells never wrap.
                      column.mono ? "type-sku whitespace-nowrap" : "type-body",
                      "[font-size:var(--text-base)]",
                      ALIGN[column.align ?? "left"],
                      selected ? "text-text-strong" : "text-text-body",
                      selected && columnIndex === 0 && "font-bold shadow-[inset_3px_0_0_var(--green-700)]",
                    )}
                  >
                    {column.render ? column.render(row) : String((row as Record<string, unknown>)[column.key] ?? "")}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
